import { useState, useEffect, useCallback } from 'react';
import { isAppInstalled, isPWAInstallable } from '../utils/pwaInstall';

/**
 * Custom hook to handle PWA installation
 * @returns {Object} - Object containing installation state and methods
 * @property {boolean} canInstall - Whether the app can be installed
 * @property {boolean} isInstalled - Whether the app is already installed
 * @property {Function} installApp - Function to trigger the installation prompt
 * @property {boolean} isPWAInstallable - Whether the browser supports PWA installation
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  // Check if the app is installed and update state
  const checkInstalled = useCallback(() => {
    const installed = isAppInstalled();
    setIsInstalled(installed);
    if (installed) {
      setCanInstall(false);
    }
    return installed;
  }, []);

  // Handle the beforeinstallprompt event
  const handleBeforeInstallPrompt = useCallback((e) => {
    // Prevent the default browser install prompt
    e.preventDefault();
    
    // Stash the event so it can be triggered later
    setDeferredPrompt(e);
    
    // Update UI to notify the user they can install the PWA
    setCanInstall(true);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('beforeinstallprompt event fired');
    }
  }, []);

  // Handle the appinstalled event
  const handleAppInstalled = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('App was installed');
    }
    setIsInstalled(true);
    setCanInstall(false);
    setDeferredPrompt(null);
  }, []);

  // Set up event listeners and check initial state
  useEffect(() => {
    // Check if PWA is installable
    const installable = isPWAInstallable();
    setIsInstallable(installable);

    // Check if the app is already installed
    checkInstalled();

    // Set up event listeners if PWA is installable
    if (installable) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
    }

    // Check for updates to the installed state
    const visibilityChangeHandler = () => {
      if (!document.hidden) {
        checkInstalled();
      }
    };
    
    document.addEventListener('visibilitychange', visibilityChangeHandler);

    // Clean up event listeners
    return () => {
      if (installable) {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      }
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
  }, [checkInstalled, handleBeforeInstallPrompt, handleAppInstalled]);

  /**
   * Trigger the PWA installation prompt
   * @returns {Promise<boolean>} Whether the installation was successful
   */
  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No deferred prompt available');
      }
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      // We've used the prompt, and can't use it again, throw it away
      setDeferredPrompt(null);
      
      if (outcome === 'accepted') {
        if (process.env.NODE_ENV === 'development') {
          console.log('User accepted the install prompt');
        }
        setIsInstalled(true);
        setCanInstall(false);
        return true;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('User dismissed the install prompt');
      }
      return false;
    } catch (error) {
      console.error('Error during installation:', error);
      return false;
    }
  }, [deferredPrompt]);

  return { 
    canInstall, 
    isInstalled, 
    installApp, 
    isPWAInstallable: isInstallable 
  };
}
